import { useId } from "react";

export type LinkPageStrokeIconName = "location" | "phone" | "arrow";

interface ILinkPageStrokeIconProps {
  name: LinkPageStrokeIconName;
  className?: string;
  /** Outline thickness in viewBox units (24 wide). Thin on purpose. */
  strokeWidth?: number;
}

/** Outline-only paths on a 24x24 grid (Feather-style); nothing is filled. */
const PATHS: Record<LinkPageStrokeIconName, string[]> = {
  location: [
    "M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z",
    "M12 12.2a2.7 2.7 0 1 0 0-5.4 2.7 2.7 0 0 0 0 5.4z",
  ],
  phone: [
    "M5 4h3.5l1.7 4.3-2.1 1.3a11 11 0 0 0 5.3 5.3l1.3-2.1L19 14.5V18a2 2 0 0 1-2 2A13 13 0 0 1 3 6a2 2 0 0 1 2-2z",
  ],
  arrow: ["M5 12h14", "m13 6 6 6-6 6"],
};

/**
 * Icon for the public link page: stroke only, with a still, subtle grey gradient on the outline
 * (light silver at the top left fading to a darker grey). The page is always a dark glass
 * surface, so the greys are chosen to read on it; there is no glow and nothing animates.
 *
 * Each instance owns its gradient id, because ids are document-global and several icons share
 * the page.
 */
export const LinkPageStrokeIcon = ({
  name,
  className,
  strokeWidth = 1.5,
}: ILinkPageStrokeIconProps) => {
  const gradientId = `link-page-stroke-${useId().replace(/:/g, "")}`;

  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
      data-icon={name}
    >
      <defs>
        <linearGradient
          id={gradientId}
          x1="3"
          y1="3"
          x2="21"
          y2="21"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#e4e6ea" />
          <stop offset="1" stopColor="#8d929b" />
        </linearGradient>
      </defs>
      {PATHS[name].map((d) => (
        <path
          key={d}
          d={d}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
};
